import React, {
  useState, memo, useMemo, useEffect, useRef,
} from 'react'
import ReactMapGL, { Marker, NavigationControl, Popup } from 'react-map-gl'
import Box from '@mui/material/Box'
import Image from 'next/image'
import PinCard from 'components/PinCard'
import { useModal } from 'components/Modal'
import dynamic from 'next/dynamic'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { getFilterObject, parseFiltersToUrl } from 'filters'
import { resetFilters, setFilters } from 'redux/modules/map'
import { useRouter } from 'next/router'
import { useMediaQuery } from '@mui/material'
import { getCenter } from 'geolib'
import HistoriasTradicionesPath from 'public/img/historias-de-tradiciones.png'
import { useIntl } from 'react-intl'
import useNonInitialEffect from 'hooks/useNonInitialEffect'
import ActionButtons from './Filters/ActionButtons'
import FiltersRow from './FiltersRow'
import sx from './styles'
import CategoriesRow from './CategoriesRow'

const FiltersDynamic = dynamic(() => import('./Filters'))

const Map = ({
  items = [],
  variant = 'map', // home|map
}) => {
  // const [coordinates, setCoordinates] = useState([])
  const mapRef = useRef()
  const router = useRouter()
  const dispatch = useDispatch()
  const { formatMessage: f } = useIntl()
  const { filters } = useSelector((s) => s.map)
  const center = { latitude: 23.895461, longitude: -102.389020 }
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const basePath = variant === 'map' ? f({ id: 'ACTIVITIES_ROUTE' }) : f({ id: 'MAIN_ROUTE' })
  const [selectedLocation, setSelectedLocation] = useState(null)
  const { handleOpenModal, handleCloseModal } = useModal()

  const bounds = [
    [-122.523579, 10.754591], // Southwest coordinates
    [-81.742328, 33.767331], // Northeast coordinates
  ]

  // const center = {
  //   latitude: 25.786348,
  //   longitude: -101.8808728,
  // }

  const [viewport, setViewport] = useState({
    width: '100%',
    height: '100vh',
    latitude: center?.latitude,
    longitude: center?.longitude,
    zoom: 5,
    minZoom: 5,
    bearing: 0,
    pitch: 0,
    maxPitch: 0,
    minPitch: 0,
    dragPan: false,
    dragRotate: false,
    touchPitch: false,
    scrollZoom: false,
    boxZoom: false,
    maxBounds: bounds,
  })

  const handleClickLocation = (location) => {
    setSelectedLocation(location)
  }

  const handleSubmit = (filtersData = {}) => {
    const filtersFinal = isEmpty(filtersData) ? filters : filtersData
    const url = parseFiltersToUrl(filtersFinal)
    router.push(`${basePath}${url}`, null, { shallow: true })
    handleCloseModal()
  }

  const handleResetAllFilters = () => {
    dispatch(resetFilters())
    router.push(`${basePath}`, null, { shallow: true })
  }

  useNonInitialEffect(() => {
    const coordinatesData = items?.map(
      (item) => ({ latitude: Number(item?.latitude), longitude: Number(item?.longitude) }),
    )
    const centerMap = getCenter(coordinatesData) || { latitude: 23.895461, longitude: -102.389020 }
    mapRef.current?.flyTo({
      center: [centerMap?.longitude, centerMap?.latitude],
      duration: 2500,
      zoom: 5.5,
      scrollZoom: false,
      boxZoom: false,
    })
  }, [items])

  useEffect(() => {
    if (!router.isReady) return
    const initialFilters = getFilterObject(router.query, '_')
    dispatch(setFilters(initialFilters))
  }, [router.isReady])

  const handleClickModalFilter = () => {
    handleOpenModal({
      title: 'FILTER_BY',
      body: (
        <FiltersDynamic
          onCloseModal={handleCloseModal}
        />
      ),
      actionButtons: (
        <ActionButtons
          handleCleanFilters={handleResetAllFilters}
          onSubmit={handleSubmit}
        />
      ),
      configProps: {
        isFormattedTitle: true,
        isDividerHeader: true,
        isDividerBottom: true,
        showClose: true,
        maxWidth: 'sm',
        fullScreen: isMobile,
        sx: sx.modalFilterContainer,
      },
      isDismissible: true,
    })
  }

  const pins = useMemo(
    () => items?.map((item) => (
      <Marker
        style={{ cursor: 'pointer' }}
        key={`acticity-${item?.slug}`}
        longitude={item?.longitude}
        latitude={item?.latitude}
        anchor="top"
        offsetLett={-20}
        offsetTop={-10}
        onClick={(e) => {
          // If we let the click event propagates to the map, it will immediately close the popup
          // with `closeOnClick: true`
          e.originalEvent.stopPropagation()
          handleClickLocation(item)
        }}
      >

        <Box sx={sx.pin}>
          <Image
            src={item?.main_image}
            layout="fill"
            objectFit="cover"
            title={`${item?.name}`}
            alt={`${item?.name}`}
            unoptimized
          />
        </Box>
      </Marker>

    )),
    [items, router?.query],
  )

  return (
    <Box
      sx={sx.root}
      id="Mapa"
    >
      <Box sx={sx.degradeContainer} />
      <ReactMapGL
        ref={mapRef}
        {...viewport}
        reuseMaps
        onMove={(evt) => setViewport(evt.viewport)}
        mapStyle="mapbox://styles/macc2630/ckf9qx6gc2npz19ntrnmyx41y"
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_KEY}
      >
        <NavigationControl position="top-left" />

        {pins}

        {selectedLocation && (
          <Popup
            anchor="bottom"
            longitude={Number(selectedLocation.longitude)}
            latitude={Number(selectedLocation.latitude)}
            onClose={() => setSelectedLocation(null)}
          >
            <PinCard item={selectedLocation} />
          </Popup>
        )}
      </ReactMapGL>

      {!isMobile && (
        <Box sx={sx.imageContainer}>
          <Box sx={sx.image}>
            <Image
              src={HistoriasTradicionesPath}
              alt="historias de méxico"
              layout="fill"
              objectFit="contain"
            />
          </Box>
        </Box>
      )}

      <FiltersRow
        onChange={handleSubmit}
        onClickModalFilter={handleClickModalFilter}
        onResetAllFilters={handleResetAllFilters}
      />

      <CategoriesRow
        categoryFilters={filters?.category}
        onChange={handleSubmit}
      />
    </Box>
  )
}

export default memo(Map)
